// Delegated click handler on #ProductGridContainer that
// inserts a YouTube iframe into any clicked .lw-video element.
// Works across motion-list re-renders and infinite scroll.

(() => {
  const CONTAINER_ID = 'ProductGridContainer';
  const TARGET_SELECTOR = '.lw-video';
  const IFRAME_CLASS = 'lw-video-iframe';
  let currentContainer = null;
  let rootObserver = null;

  // Create an iframe for a given YouTube video id
  function createYouTubeIframe(videoId) {
    const iframe = document.createElement('iframe');
    iframe.className = IFRAME_CLASS;
    iframe.width = '100%';
    iframe.height = '100%';
    iframe.setAttribute('frameborder', '0');
    iframe.setAttribute('allowfullscreen', '');
    // Allow autoplay, PIP, etc.
    iframe.setAttribute('allow', 'autoplay; encrypted-media; picture-in-picture; fullscreen');

    const params = new URLSearchParams({
      rel: '0',
      autoplay: '1',
      playsinline: '1',
    });

    // Note: we validate the id; if valid we can safely include it in the path.
    iframe.src = `https://www.youtube.com/embed/${encodeURIComponent(videoId)}?${params.toString()}`;

    // Make sure it covers the entire lw-video box
    iframe.style.position = 'absolute';
    iframe.style.top = '0';
    iframe.style.left = '0';
    iframe.style.width = '100%';
    iframe.style.height = '100%';

    return iframe;
  }

  // Simple YouTube video ID validation (11 chars: letters, numbers, _ or -)
  function isValidYouTubeId(id) {
    return typeof id === 'string' && /^[A-Za-z0-9_-]{11}$/.test(id);
  }

  // Delegated click handler
  function onContainerClick(e) {
    const videoEl = e.target && e.target.closest
      ? e.target.closest(TARGET_SELECTOR)
      : null;

    if (!videoEl || !currentContainer) return;

    // Ensure .lw-video is inside a <motion-list> within the current container
    const motionList = videoEl.closest('motion-list');
    if (!motionList || !currentContainer.contains(motionList)) return;

    // If an iframe is already present, do nothing (avoid duplicates)
    if (videoEl.querySelector(`iframe.${IFRAME_CLASS}`)) return;

    // Read the YouTube video id from data-yt-video-id
    const videoId = videoEl.getAttribute('data-yt-video-id') || videoEl.dataset.ytVideoId;
    if (!isValidYouTubeId(videoId)) return; // Ignore if missing/invalid

    // Insert the iframe
    const iframe = createYouTubeIframe(videoId);
    videoEl.appendChild(iframe);

    // Optional: prevent default if the container is an <a>
    if (e.target.closest('a')) {
      e.preventDefault();
    }
  }

  function bindToContainer(container) {
    if (!container) return;
    if (currentContainer === container) return; // already bound

    // Unbind old container
    if (currentContainer) {
      currentContainer.removeEventListener('click', onContainerClick, true);
    }

    // Bind new container (capture phase for resilience)
    container.addEventListener('click', onContainerClick, true);
    currentContainer = container;
  }

  function unbindContainer() {
    if (currentContainer) {
      currentContainer.removeEventListener('click', onContainerClick, true);
      currentContainer = null;
    }
  }

  function findContainer() {
    return document.getElementById(CONTAINER_ID);
  }

  function ensureBound() {
    const container = findContainer();
    if (container) {
      bindToContainer(container);
    } else {
      unbindContainer();
    }
  }

  function startRootObserver() {
    if (rootObserver) return;

    rootObserver = new MutationObserver(() => {
      const container = findContainer();
      if (container !== currentContainer) {
        ensureBound();
      }
    });

    rootObserver.observe(document.documentElement || document.body, {
      childList: true,
      subtree: true,
    });
  }

  function init() {
    ensureBound();
    startRootObserver();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
